<template>
  <div class="py-2 px-4">
    <v-row class="px-3">
      <v-col cols="3">
        <v-checkbox
          v-model="haveDigitalAccount"
          label="Compte digital"
          color="third"
          hide-details
        />
        <v-checkbox
          v-model="haveLicence"
          label="Licences"
          color="third"
          hide-details
        />
      </v-col>
      <v-col cols="3">
        <v-checkbox
          v-model="haveInsurance"
          label="Assurance annuel"
          color="third"
          hide-details
        />
        <v-checkbox
          v-model="showParticipantFromInstructorSessions"
          label="Les clients sont dans mes sessions "
          color="third"
          hide-details
        />
      </v-col>
      <v-col cols="6" class="pa-3">
        <v-range-slider
          v-model="ageRange"
          :max="maxAge"
          :min="minAge"
          hide-details
          thumb-label
          thumb-color="third"
          track-color="third"
          track-fill-color="third"
          class="pt-4 align-center"
        >
          <template #prepend>
            <span>Age:</span>
          </template>
        </v-range-slider>
        <v-text-field
          v-model="search"
          color="third"
          flat
          hide-details
          clearable
          append-icon="mdi-magnify"
          class="pt-3"
          label="Recherche"
        />
      </v-col>
      <v-col cols="6">
        <v-row justify="center">
          <v-expansion-panels
            v-model="panel"
            class="pa-3"
          >
            <v-expansion-panel>
              <v-expansion-panel-header>
                Séances
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-text-field
                  v-model="searchSessions"
                  color="third"
                  append-icon="mdi-magnify"
                  label="Nom de la session"
                  single-line
                  hide-details
                  class="pb-4 pt-0 mt-0"
                  @click.stop
                />
                <v-data-table
                  v-model="selectedSession"
                  :search="searchSessions"
                  :headers="sessionsHeaders"
                  :items="sessionsData"
                  item-key="uuid"
                  item-text="title"
                  :items-per-page="5"
                  show-select
                  checkbox-color="third"
                  @toggle-select-all="selectAllSessions"
                  @click:row="rowClick"
                  @item-selected="selectItem"
                />
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-row>
      </v-col>
      <v-col cols="3">
        <ContentPanel title="Niveau" :items="customersLevels" @updateContent="updateSelectedLevel" />
      </v-col>
      <v-col cols="3">
        <ContentPanel title="Produit" :items="products" @updateContent="updateSelectedProduct" />
      </v-col>
    </v-row>
    <v-row justify="center" class="mt-6">
      <v-btn v-if="canUserPerformAction" color="third" class="white--text" height="32" @click="searchOnAlgolia">
        Rechercher
      </v-btn>
    </v-row>
    <v-row>
      <ContentTable class="full-width" :headers="headers" :items="noSelected" :items-selected="filteredSelectedAssignCustomer" @updateSelected="updateSelected" />
    </v-row>
    <v-row justify="center" class="ma-4">
      <v-btn color="third" class="white--text" height="32" :disabled="!selectedAssignCustomer.length" @click="nextStep()">
        Confirmer les clients
      </v-btn>
    </v-row>
  </div>
</template>

<script>
import { ref, computed, inject } from '@vue/composition-api';
import { format, fromUnixTime, differenceInYears } from 'date-fns';
import { fr } from 'date-fns/esm/locale';
import ContentPanel from './ContentPanel.vue';
import ContentTable from './ContentTable.vue';
import store from '@/store';
import { searchCustomerOnAlgolia } from '@/helpers/search';
import { filterCustomer, customerNotSelected } from '@/helpers/utils';
import { getCustomersByUUids } from '@/helpers/request';

const localReferences = require('@/configs/references.json');

export default {
  components: { ContentPanel, ContentTable },
  setup (props, context) {
    const ageRange = ref([0, 99]);
    const minAge = ref(0);
    const maxAge = ref(99);
    const selectedCustomer = ref(null);
    const search = ref('');
    const selectedAssignCustomer = inject('selectedAssignCustomer');
    const alphaProducts = computed(() => store.state.products);
    const weeklySessions = computed(() => store.state.sessions);

    const panel = ref(1);

    const selectedSession = ref([]);
    const searchSessions = ref('');

    const selectedLevel = ref([]);
    const selectedProduct = ref([]);

    const sessionParticipants = ref([]);

    const products = computed(() => alphaProducts.value.map(p => ({ label: p.name, value: p.uuid })));

    const haveDigitalAccount = ref(false);
    const haveLicence = ref(false);
    const haveInsurance = ref(false);
    const showParticipantFromInstructorSessions = ref(false);

    const currentWorkspace = computed(() => store.state.workspace);

    const headers = inject('clientHeaders');

    const sessionsHeaders = [
      { text: 'Nom', value: 'title', align: 'start', width: '60%' },
      { text: 'Date de la session', value: 'date', align: 'end', width: '40%' }
    ];

    const computeCustomerData = customer => {
      return {
        ...customer,
        digital_account: customer.digital_account ? 'Oui' : 'Non',
        age: customer.birth_date ? differenceInYears(new Date(), new Date(`${customer.birth_date}`)) : '-',
        email: customer.email || null,
        equestrian_level: customer.equestrian_level,
        equestrianLevelLabel: customer.equestrian_level || '-',
        fullname: `${customer.firstname} ${customer.lastname}`
      };
    };

    const sessionsData = computed(() => weeklySessions.value.map(s => ({
      uuid: s.uuid,
      title: s.name,
      date: format(fromUnixTime(s.start_time), 'dd-MM-yyyy (EEEE)', { locale: fr })
    })));

    const filterCustomerOptions = computed(() => ({
      selectedLevel: selectedLevel.value,
      haveDigitalAccount: haveDigitalAccount.value,
      selectedProduct: selectedProduct.value,
      search: search.value,
      haveInsurance: haveInsurance.value,
      showParticipantFromInstructorSessions: showParticipantFromInstructorSessions.value,
      ageRange: ageRange.value
    }));

    const filteredSelectedAssignCustomer = computed(() => selectedAssignCustomer.value.filter(c => filterCustomer(c, filterCustomerOptions.value)));

    const allFetchedCustomers = computed(() => {
      const algoliaCustomers = store.state.algoliaCustomers;
      const uniqueSessionParticipants = [];
      if (sessionParticipants.value.length) {
        sessionParticipants.value.map(sp => {
          if (algoliaCustomers.some(ac => ac.uuid === sp.uuid)) {
            return;
          }
          uniqueSessionParticipants.push(sp);
        });
      }
      return algoliaCustomers.concat(uniqueSessionParticipants);
    });

    const noSelected = computed(() => {
      return customerNotSelected(allFetchedCustomers.value, selectedAssignCustomer.value, filterCustomerOptions.value);
    });

    const selectedAndNoSelectedCustomers = computed(() => [...noSelected.value, ...selectedAssignCustomer.value]);

    const rowClick = (item, row, event) => {
      event.stopPropagation();
      row.select(!row.isSelected);
    };

    const getSessionParticipantsDetail = async items => {
      const uuids = [];
      items.map(item => {
        const sessionDetails = store.getters.session(item.uuid);
        (sessionDetails.participants || []).filter(p => !p.isGuest && !selectedAndNoSelectedCustomers.value.find(v => v.uuid === p.uuid)).map(participant => uuids.push(participant.uuid));
      });

      if (uuids.length) {
        return getCustomersByUUids([...new Set(uuids)], ['equestrian_level', 'digital_account']);
      }

      return [];
    };

    const selectItem = async ({ item, value, selectAll, response = null }) => {
      const sessionDetails = store.getters.session(item.uuid);

      const participants = sessionDetails.participants.filter(p => !p.isGuest);
      if (!participants.length) {
        return;
      }

      let auxiliarResponse = { ...response };

      if (!selectAll && value) {
        auxiliarResponse = await getSessionParticipantsDetail([item]);
      }

      participants.map(participant => {
        if (value) {
          if (!selectedAndNoSelectedCustomers.value.some(cust => cust.uuid === participant.uuid)) {
            let customer;
            if (auxiliarResponse && auxiliarResponse.success) {
              customer = auxiliarResponse.data.items.find(c => c.uuid === participant.uuid);
            }

            sessionParticipants.value.push({
              ...participant,
              ...computeCustomerData(customer),
            });
          }
        } else {
          const indexParticipant = sessionParticipants.value.findIndex(v => v.uuid === participant.uuid);
          if (indexParticipant !== -1) {
            sessionParticipants.value.splice(indexParticipant, 1);
          }
        }
      });
    };

    const selectAllSessions = async ({ items, value }) => {
      const response = await getSessionParticipantsDetail(items);
      items.map(item => {
        selectItem({ item, value, selectAll: true, response });
      });
    };

    const filterBuilder = (singleField, filterKey, filterValue) => {
      if (singleField) {
        if (Array.isArray(filterValue) && filterValue.length) {
          // alpha_mo alpha_mo_test
          const values = filterValue.join(` OR ${filterKey}: `);
          return `(${filterKey}: ${values})`;
        }
        return `${filterKey}: ${filterValue}`;
      }

      if (Array.isArray(filterValue) && filterValue.length) {
        const customerFilter = filterValue.join(`" OR ${filterKey}: "`);

        return `(${filterKey}: "${customerFilter}")`;
      }

      return null;
    };

    const fetchInstructorSessionsCustomers = async () => {
      const customersToFetch = [];
      store.state.sessions.forEach(s => {
        const foundInstructor = s.instructors.find(instructor => instructor.uuid === store.state.user.uuid);
        if (foundInstructor) {
          (s.participants || []).forEach(p => {
            const isParticipantOnArray = store.state.algoliaCustomers.some(c => c.uuid === p.uuid) || customersToFetch.some(uuid => uuid === p.uuid);
            if (!isParticipantOnArray && !p.isGuest) {
              customersToFetch.push(p.uuid);
            }
          });
        }
      });
      if (customersToFetch.length) {
        const customers = await getCustomersByUUids(customersToFetch, ['equestrian_level', 'digital_account']);
        (customers?.data?.items || []).forEach(c => {
          sessionParticipants.value.push({
            ...computeCustomerData(c),
          });
        });
      }
    };

    const searchOnAlgolia = async () => {
      if (!store.getters.canUserPerformAction) {
        return;
      }
      const parts = currentWorkspace.value.code.split('_test');

      let filter = '';

      filter += filterBuilder(true, 'customer_workspaces', [parts[0], currentWorkspace.value.code]);
      filter += ` AND ${filterBuilder(true, 'wtype', 'customer')}`;
      // filter += ` AND ${filterBuilder(true, 'digital_account', haveDigitalAccount.value)}`;

      const equestrianLevelFilter = filterBuilder(false, 'equestrian_level', selectedLevel.value);

      filter += equestrianLevelFilter ? ` AND ${equestrianLevelFilter}` : '';

      const allowedProductFilter = filterBuilder(false, 'allowed_products.product_uuid', selectedProduct.value);

      filter += allowedProductFilter ? ` AND ${allowedProductFilter}` : '';

      await searchCustomerOnAlgolia(search.value, filter);
      if (showParticipantFromInstructorSessions.value) {
        await fetchInstructorSessionsCustomers();
      }
    };

    const customersLevels = computed(() => {
      const levels = [];
      allFetchedCustomers.value.map(customer => {
        if (customer.equestrian_level) {
          const index = levels.findIndex(l => l.value === customer.equestrian_level);

          if (index !== -1) {
            levels[index].count += 1;
            return;
          }
          levels.push({ label: customer.equestrian_level, value: customer.equestrian_level, count: 1 });
        }
      });
      return levels;
    });

    const updateSelected = items => {
      const { selected, deselected, action } = items;

      switch (action) {
        case 'select':
          // all deselected to selected
          deselected.map(item => {
            const indexNonSelected = sessionParticipants.value.findIndex(c => c.uuid === item.uuid);
            if (indexNonSelected !== -1) {
              sessionParticipants.value.splice(indexNonSelected, 1);
            }
            selectedAssignCustomer.value.push(item);
          });
          break;
        case 'deselect':
          // all selected to deselected
          selected.map(item => {
            const indexSelectedAssign = selectedAssignCustomer.value.findIndex(c => c.uuid === item.uuid);
            if (indexSelectedAssign !== -1) {
              selectedAssignCustomer.value.splice(indexSelectedAssign, 1);
            }
          });
          break;
        default:
          break;
      }
    };

    const updateSelectedLevel = tableItem => {
      selectedLevel.value = tableItem.map(item => item.value);
    };

    const updateSelectedProduct = tableItem => {
      selectedProduct.value = tableItem.map(item => item.value);
    };

    const updateSelectedSession = tableItem => {
      selectedSession.value = tableItem.map(item => item.value);
    };

    const nextStep = () => {
      context.emit('next-step', localReferences.productStepper.PRODUCTS);
    };

    const canUserPerformAction = computed(() => store.getters.canUserPerformAction);

    return {
      updateSelectedSession,
      selectItem,
      selectAllSessions,
      rowClick,
      searchSessions,
      selectedSession,
      panel,
      sessionsHeaders,
      sessionsData,
      canUserPerformAction,
      products,
      alphaProducts,
      haveDigitalAccount,
      haveLicence,
      haveInsurance,
      showParticipantFromInstructorSessions,
      filteredSelectedAssignCustomer,
      updateSelectedLevel,
      updateSelectedProduct,
      updateSelected,
      ageRange,
      minAge,
      maxAge,
      selectedCustomer,
      search,
      searchOnAlgolia,
      customersLevels,
      headers,
      noSelected,
      selectedAssignCustomer,
      nextStep
    };
  }
};

</script>
